import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Crosshair, Loader2, Skull, Trophy, Users, Zap } from "lucide-react";
import { plyxApi as api } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { millionsAudio } from "@/lib/millionsAudio";
import { toast } from "sonner";
import { XpBar } from "@/pages/plyx/PlyxBrainClash";

const AliveBadge = ({ n, total }) => (
  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FF2D7A]/15 border border-[#FF2D7A]/40 text-xs font-extrabold" data-testid="survivor-alive-count">
    <Users className="w-3.5 h-3.5 text-[#FF2D7A]" /> {n} standing{total ? ` / ${total}` : ""}
  </div>
);

export default function PlyxSurvivor() {
  const { user } = usePlyxAuth();
  const navigate = useNavigate();
  const [arena, setArena] = useState(null);
  const [secs, setSecs] = useState(0);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(null);
  const [champions, setChampions] = useState([]);
  const arenaIdRef = useRef(null);
  const lastQRef = useRef(-1);

  const apply = useCallback((data) => {
    setArena(data);
    setSecs(data.seconds_left);
    if (data.round !== lastQRef.current && data.phase === "question") {
      lastQRef.current = data.round;
      setPicked(null);
    }
  }, []);

  const fetchCurrent = useCallback(() => {
    api.get("/plyx/survivor/arena").then((r) => {
      arenaIdRef.current = r.data.arena_id;
      apply(r.data);
    }).catch(() => {});
  }, [apply]);

  useEffect(() => {
    fetchCurrent();
    api.get("/plyx/survivor/champions").then((r) => setChampions(r.data.champions || [])).catch(() => {});
  }, [fetchCurrent]);

  useEffect(() => {
    const t = setInterval(() => {
      if (!arenaIdRef.current) return;
      api.get(`/plyx/survivor/arena/${arenaIdRef.current}`).then((r) => apply(r.data)).catch(() => {});
    }, 1500);
    return () => clearInterval(t);
  }, [apply]);

  useEffect(() => {
    const t = setInterval(() => setSecs((s) => {
      const n = Math.max(0, s - 1);
      if (arena?.phase === "question" && n <= 3 && n > 0) millionsAudio.tick(true);
      return n;
    }), 1000);
    return () => clearInterval(t);
  }, [arena?.phase]);

  const join = async () => {
    if (!user) { navigate("/plyx/login"); return; }
    millionsAudio.ensure();
    setBusy(true);
    try {
      await api.post(`/plyx/survivor/arena/${arenaIdRef.current}/join`);
      toast.success("You're in the arena — survive!");
      const r = await api.get(`/plyx/survivor/arena/${arenaIdRef.current}`);
      apply(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join");
    } finally { setBusy(false); }
  };

  const answer = async (choice) => {
    if (picked != null || arena.my_choice != null) return;
    millionsAudio.ensure();
    setPicked(choice);
    try {
      await api.post(`/plyx/survivor/arena/${arenaIdRef.current}/answer`, { round: arena.round, choice });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Answer not accepted");
    }
  };

  const predict = async (pid) => {
    try {
      await api.post(`/plyx/survivor/arena/${arenaIdRef.current}/predict`, { player_id: pid });
      toast.success("Prediction locked — +30 XP if they win!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Prediction failed");
    }
  };

  if (!arena) {
    return (
      <div className="min-h-screen millions-stage text-white flex items-center justify-center" data-testid="survivor-loading">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Entering the arena…
      </div>
    );
  }

  const pid = user?.plyx_user_id;
  const inLobby = arena.phase === "lobby" || arena.phase === "starting";
  const playing = arena.phase === "question" || arena.phase === "reveal";
  const eliminated = arena.is_member && !arena.is_alive && arena.status !== "finished" && !inLobby;

  return (
    <div className="min-h-screen millions-stage text-white pb-24" data-testid="plyx-survivor">
      <div className="max-w-2xl mx-auto px-4 pt-5">
        <div className="flex items-center justify-between mb-3">
          <Link to="/plyx/games" className="flex items-center gap-1 text-white/60 text-sm" data-testid="survivor-back-games">
            <ArrowLeft className="w-4 h-4" /> All games
          </Link>
          {playing && <AliveBadge n={arena.alive_count} total={arena.players_count} />}
        </div>

        {inLobby && (
          <div data-testid="survivor-lobby">
            <h1 className="text-3xl font-extrabold flex items-center gap-2">
              <Skull className="w-7 h-7 text-[#FF2D7A]" /> Plyx Survivor
            </h1>
            <p className="text-sm text-white/60 mt-1 mb-4">
              Everyone gets the SAME question. Answer wrong — or too slow — and you're OUT.
              Last one standing takes the crown and 150+ XP. Up to {arena.max_players} players.
            </p>
            <XpBar />

            {arena.phase === "starting" && (
              <div className="rounded-2xl bg-[#FF2D7A]/10 border border-[#FF2D7A]/40 text-center py-5 mb-4" data-testid="survivor-starting">
                <div className="text-5xl font-extrabold text-[#FF2D7A]">{Math.ceil(secs)}</div>
                <p className="text-sm text-white/70 mt-1 font-bold">Arena locks in… join NOW</p>
              </div>
            )}

            <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold">Fighters in the arena</span>
                <span className="text-xs text-white/50" data-testid="survivor-player-count">{arena.players_count} joined</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {arena.scoreboard.map((r) => (
                  <span key={r.player_id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/[0.06] border border-white/10 text-xs font-bold">
                    @{r.player?.handle}
                  </span>
                ))}
                {arena.players_count === 0 && <span className="text-xs text-white/40">Be the first one in…</span>}
              </div>
              {arena.players_count === 1 && arena.is_member && (
                <p className="text-xs text-[#FFB42D] mt-2 font-bold" data-testid="survivor-need-more">Waiting for one more fighter — share Plyx with a friend!</p>
              )}
            </div>

            {!arena.is_member ? (
              <button type="button" onClick={join} disabled={busy} data-testid="survivor-join-btn"
                className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#FF2D7A] to-[#FFB42D] text-black font-extrabold disabled:opacity-50 mb-4">
                {busy ? "Joining…" : "💀 Enter the arena"}
              </button>
            ) : (
              <p className="text-center text-sm text-[#00E0C7] font-bold mb-4" data-testid="survivor-joined">✔ You're in — arena starts when 2+ fighters have joined</p>
            )}

            {champions.length > 0 && (
              <div data-testid="survivor-champions">
                <div className="text-xs font-bold text-white/50 mb-2">RECENT CHAMPIONS</div>
                <div className="space-y-1.5">
                  {champions.slice(0, 5).map((c, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2">
                      <Trophy className="w-4 h-4 text-[#FFB42D]" />
                      <span className="font-bold flex-1">@{c.winner?.handle}</span>
                      <span className="text-xs text-white/40">beat {Math.max(0, (c.players_count || 1) - 1)} others</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {playing && (
          <div data-testid="survivor-play">
            {eliminated && (
              <div className="rounded-xl bg-[#FF2D7A]/15 border border-[#FF2D7A]/40 text-center text-sm font-bold py-2 mb-3" data-testid="survivor-eliminated-banner">
                💀 You fell — spectate and predict the champion
              </div>
            )}
            <div className="flex items-center justify-between text-xs text-white/50 mb-1.5">
              <span>Round {arena.question?.number} of {arena.rounds_total}</span>
              <span className="font-extrabold text-white">{arena.phase === "question" ? `${Math.ceil(secs)}s` : "Answers locked"}</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/10 mb-3 overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-1000 ${secs <= 3 ? "bg-[#FF2D7A]" : "bg-[#FFB42D]"}`}
                style={{ width: arena.phase === "question" ? `${(secs / arena.question_secs) * 100}%` : "0%" }} />
            </div>
            {arena.question && (
              <>
                <div className="rounded-2xl millions-qcard p-5 mb-4">
                  <h2 className="text-lg font-extrabold leading-snug" data-testid="survivor-question">{arena.question.q}</h2>
                </div>
                <div className="grid gap-2.5 mb-4">
                  {arena.question.options.map((opt, i) => {
                    const mine = picked != null ? picked : arena.my_choice;
                    const isMine = mine === i;
                    const isCorrect = arena.phase === "reveal" && arena.correct_index === i;
                    const isWrongPick = arena.phase === "reveal" && isMine && arena.correct_index !== i;
                    return (
                      <button key={i} type="button" onClick={() => answer(i)} data-testid={`survivor-option-${i}`}
                        disabled={arena.phase !== "question" || mine != null || !arena.is_alive}
                        className={`text-left px-4 py-3 rounded-xl border text-sm font-semibold transition-colors ${
                          isCorrect ? "bg-[#00E0C7]/20 border-[#00E0C7] text-[#00E0C7]"
                          : isWrongPick ? "bg-[#FF2D7A]/20 border-[#FF2D7A] text-[#FF2D7A]"
                          : isMine ? "bg-[#FFB42D]/20 border-[#FFB42D]"
                          : "bg-white/[0.06] border-white/10 hover:border-white/30 disabled:opacity-60"
                        }`}>
                        <span className="text-[#FFB42D] font-extrabold mr-2">{"ABCD"[i]}.</span>{opt}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            {arena.phase === "reveal" && arena.fell_this_round.length > 0 && (
              <div className="rounded-xl bg-[#FF2D7A]/10 border border-[#FF2D7A]/30 px-3 py-2 mb-3 text-sm" data-testid="survivor-fell">
                💀 <span className="font-bold">{arena.fell_this_round.map((p) => `@${p.handle}`).join(", ")}</span> fell this round
              </div>
            )}
            {arena.prediction_open && !arena.my_prediction && (
              <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-3 mb-3" data-testid="survivor-predict">
                <div className="text-xs font-bold text-white/60 mb-2 flex items-center gap-1">
                  <Crosshair className="w-3.5 h-3.5 text-[#FFB42D]" /> PREDICT THE CHAMPION — +30 XP if you're right
                </div>
                <div className="flex flex-wrap gap-2">
                  {arena.scoreboard.filter((r) => r.alive).map((r) => (
                    <button key={r.player_id} type="button" onClick={() => predict(r.player_id)}
                      data-testid={`survivor-predict-${r.player_id}`}
                      className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-[#FFB42D]/20 border border-white/10 text-xs font-bold">
                      @{r.player?.handle}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {arena.my_prediction && (
              <p className="text-center text-xs text-[#FFB42D] font-bold mb-3" data-testid="survivor-prediction-locked">🎯 Prediction locked</p>
            )}
            <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-3" data-testid="survivor-scoreboard">
              <div className="text-xs font-bold text-white/50 mb-2">SURVIVORS</div>
              <div className="space-y-1.5">
                {arena.scoreboard.map((r) => (
                  <div key={r.player_id}
                    className={`flex items-center gap-2 text-sm rounded-lg px-2 py-1 ${r.player_id === pid ? "bg-[#FFB42D]/10 border border-[#FFB42D]/30" : ""} ${!r.alive ? "opacity-40" : ""}`}
                    data-testid={`survivor-row-${r.player_id}`}>
                    <span className="font-bold truncate flex-1">@{r.player?.handle}</span>
                    {!r.alive ? <span className="text-[10px] font-bold text-[#FF2D7A]">OUT R{(r.elim_round ?? 0) + 1}</span>
                      : <span className="text-[10px] font-bold text-[#00E0C7]">ALIVE</span>}
                    <span className="font-extrabold text-sm w-12 text-right">{r.points}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {arena.status === "finished" && (
          <div data-testid="survivor-finished">
            <div className="text-center mb-5">
              <div className="text-6xl mb-2">👑</div>
              <h1 className="text-2xl font-extrabold">We have a Survivor!</h1>
              {arena.winner && (
                <p className="text-sm text-white/60 mt-1">
                  <span className="font-extrabold text-[#FFB42D]">@{arena.winner.handle}</span> outlasted {Math.max(0, arena.players_count - 1)} fighters
                </p>
              )}
            </div>
            <div className="space-y-2 mb-5">
              {(arena.results || []).map((r) => (
                <div key={r.player_id}
                  className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border ${r.place === 1 ? "bg-[#FFB42D]/10 border-[#FFB42D]/40" : "bg-white/[0.04] border-white/10"} ${r.player_id === pid ? "ring-1 ring-[#00E0C7]/50" : ""}`}
                  data-testid={`survivor-result-${r.player_id}`}>
                  <span className="text-lg">{r.place === 1 ? "👑" : r.place === 2 ? "🥈" : r.place === 3 ? "🥉" : `${r.place}.`}</span>
                  <span className="font-bold flex-1 truncate">@{r.player?.handle}</span>
                  {r.elim_round != null && <span className="text-[10px] text-white/40">out R{r.elim_round + 1}</span>}
                  <span className="text-xs font-bold text-[#00E0C7] inline-flex items-center gap-0.5"><Zap className="w-3 h-3" />+{r.xp_awarded} XP</span>
                </div>
              ))}
            </div>
            {arena.my_prediction && (
              <p className="text-center text-sm mb-4" data-testid="survivor-prediction-result">
                {arena.my_prediction === arena.winner?.plyx_user_id
                  ? <span className="text-[#00E0C7] font-bold">🎯 Your prediction hit — +30 XP!</span>
                  : <span className="text-white/50">Your prediction missed this time.</span>}
              </p>
            )}
            <div className="flex gap-3 justify-center">
              <button type="button" onClick={() => { lastQRef.current = -1; setPicked(null); fetchCurrent(); }} data-testid="survivor-next-arena"
                className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#FF2D7A] to-[#FFB42D] text-black font-extrabold text-sm">Enter next arena</button>
              <Link to="/plyx/games" data-testid="survivor-to-games"
                className="px-6 py-2.5 rounded-full bg-white/10 font-bold text-sm">All games</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
